import {Box, Button, Chip, CircularProgress, Paper, Stack, Typography} from '@mui/material'
import {
  AutoAwesomeOutlined as AutoAwesomeOutlinedIcon,
  DownloadOutlined as DownloadOutlinedIcon,
  UploadOutlined as UploadOutlinedIcon,
  RefreshOutlined as RefreshOutlinedIcon,
} from '@mui/icons-material'
import type {AppArtifactBuilderSkillMetadata} from '../services/AppArtifactBuilderSkillService'
import {ds} from './agent-builder/theme'

interface AppArtifactBuilderCardProps {
  metadata: AppArtifactBuilderSkillMetadata | null
  loading: boolean
  downloading: boolean
  error: string | null
  onDownload: () => void
  onUpload: () => void
  onRetry: () => void
}

export function AppArtifactBuilderCard({
  metadata,
  loading,
  downloading,
  error,
  onDownload,
  onUpload,
  onRetry,
}: AppArtifactBuilderCardProps) {
  return (
    <Paper
      id="app-artifact-builder"
      variant="outlined"
      sx={{
        p: {xs: 2, md: 2.5},
        mb: 3,
        borderRadius: ds.radiusLg,
        borderColor: 'rgba(0,143,168,0.32)',
        background: theme =>
          `linear-gradient(135deg, ${theme.palette.background.paper} 0%, rgba(0,143,168,0.045) 100%)`,
        scrollMarginTop: 24,
      }}
    >
      <Stack direction={{xs: 'column', md: 'row'}} spacing={2.5} alignItems={{md: 'flex-start'}}>
        <Box sx={{
          width: 44,
          height: 44,
          borderRadius: ds.radiusSm,
          bgcolor: 'rgba(0,143,168,0.12)',
          color: 'primary.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
          <AutoAwesomeOutlinedIcon />
        </Box>

        <Box sx={{flex: 1, minWidth: 0}}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography sx={{fontSize: '1.05rem', fontWeight: 700}}>
              {metadata?.name ?? 'Ros App Artifact Builder'}
            </Typography>
            {metadata && <Chip size="small" label={`v${metadata.version}`} variant="outlined" />}
            <Chip size="small" label="Downloadable skill" color="primary" variant="outlined" />
          </Stack>
          <Typography sx={{color: 'text.secondary', fontSize: '0.875rem', lineHeight: 1.55, mt: 0.75, maxWidth: 820}}>
            {metadata?.description ?? "Use the Ros builder skill to convert a dashboard while preserving the user's original UI/UX exactly, then upload the finished App Artifact ZIP here."}
          </Typography>

          {loading ? (
            <Stack direction="row" spacing={1} alignItems="center" sx={{mt: 2, color: 'text.secondary'}}>
              <CircularProgress size={16} />
              <Typography variant="body2">Loading builder details…</Typography>
            </Stack>
          ) : error ? (
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{mt: 2}}>
              <Typography variant="body2" color="error.main">{error}</Typography>
              <Button size="small" startIcon={<RefreshOutlinedIcon />} onClick={onRetry}>Retry</Button>
            </Stack>
          ) : metadata ? (
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))'},
              gap: 1,
              mt: 2,
            }}>
              {metadata.instructions.map(instruction => (
                <Box key={instruction.client} sx={{p: 1.25, borderRadius: ds.radiusSm, bgcolor: 'action.hover'}}>
                  <Typography sx={{fontSize: '0.75rem', fontWeight: 700, mb: 0.25}}>
                    {instruction.client}
                  </Typography>
                  <Typography sx={{fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.45}}>
                    {instruction.text}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : null}

          <Typography sx={{fontSize: '0.72rem', color: 'text.disabled', mt: 1.5}}>
            Ros hosts the finished App Artifact. The HTML conversion runs in the AI workspace where you install this skill.
          </Typography>
        </Box>

        <Stack direction={{xs: 'row', md: 'column'}} spacing={1} sx={{flexShrink: 0}}>
          <Button
            variant="contained"
            startIcon={downloading ? <CircularProgress size={15} color="inherit" /> : <DownloadOutlinedIcon />}
            disabled={!metadata || downloading}
            onClick={onDownload}
          >
            {downloading ? 'Downloading…' : 'Download skill'}
          </Button>
          <Button variant="outlined" startIcon={<UploadOutlinedIcon />} onClick={onUpload}>
            Upload finished App Artifact
          </Button>
        </Stack>
      </Stack>
    </Paper>
  )
}
